import { FC, useContext, useState } from "react";

import { Trans, useTranslation } from "react-i18next";

import { useForm } from "@mantine/form";
import { setDoc } from "firebase/firestore";
import { Flex, Button, TextInput, SimpleGrid } from "@mantine/core";

import { SettingsForm } from "@components/SettingsForm/SettingsForm";
import { PhoneNumberInput } from "@components/PhoneNumberInput/PhoneNumberInput";

import { useLanguageCode } from "@hooks/useLanguageCode";
import { getUserDocRef } from "../../../services/user.services";
import { getDefaultPhoneCodeUtil } from "@utils/getDefaultPhoneCode.util";
import { AuthContext } from "@components/AuthProvider/contexts/Auth.context";

export const GeneralInformation: FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  const { authUser, user } = useContext(AuthContext)!;

  const { t } = useTranslation();
  const lc = useLanguageCode().toUpperCase();

  const generalForm = useForm({
    initialValues: {
      lastName: user.lastName || "",
      firstName: user.firstName || "",
      phone: user.phone || { code: getDefaultPhoneCodeUtil(lc), number: "" },
    },
  });

  const handleSubmit = generalForm.onSubmit(
    async ({ firstName, lastName, phone }) => {
      setIsLoading(true);
      await setDoc(
        getUserDocRef(authUser.uid),
        { firstName, lastName, phone },
        { merge: true }
      );
      setIsLoading(false);
    }
  );

  return (
    <SettingsForm
      title={t("General Information")}
      description={
        <Trans
          i18nKey="Here you can update your profile."
          shouldUnescape={true}
          components={{ b: <b /> }}
          values={{ characters: escape("<, >, &") }}
        />
      }
      onSubmit={handleSubmit}
    >
      <SimpleGrid cols={2}>
        <TextInput
          required
          label={t("First Name")}
          {...generalForm.getInputProps("firstName")}
        />
        <TextInput
          required
          label={t("Last Name")}
          {...generalForm.getInputProps("lastName")}
        />
      </SimpleGrid>
      <PhoneNumberInput
        code={generalForm.getInputProps("phone.code")}
        number={generalForm.getInputProps("phone.number")}
      />
      <TextInput
        required
        disabled
        label={t("Email Address")}
        value={authUser.email || ""}
      />

      <Flex justify="end" gap="md">
        <Button
          variant="outline"
          onClick={generalForm.reset}
          disabled={isLoading}
        >
          {t("Cancel")}
        </Button>
        <Button type="submit" loading={isLoading}>
          {t("Save")}
        </Button>
      </Flex>
    </SettingsForm>
  );
};
